/**
 * Created by vsuthar on 8/23/21
 * File: index.js
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { Bold, useModal, LoadingPage } from '@panwds/react-ui'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { LoadAcqRequests, LoadAcquisitions, LoadAllInstanceData, LoadTreeData, LoadTreeDataAsync } from '../../../common/actions/GlobalActions'
import { APP_ID_PRISMA_ACCESS_EDITION, SPARKY_ACCESS_WRITE, TSG_NONE, VIEW_MANAGE_SUBSCRIPTION, VIEW_MANAGE_TENANT } from '../../../common/constants/GlobalConstants'
import { setNewTsgLoading } from '../../../common/reducers/actions/LicenseReducer.actions'
import { loadInstanceData, selectTreeNode } from '../../../common/reducers/actions/TreeReducer.actions'
import { setCurrentViewing, loadTsgInstances, getVarValue } from '../../../common/utils/Sparky'
import { transferChildLicenseToPA, transferLicenseToPA, getCOBUsage, getSignature, isVMFlexActivationEnabled, caseInsensitiveIsEqual } from '../../../common/utils/Utils'
import { SUBSCRIPTION_ACTION_EDIT } from '../../../subscription/components/constants/SubscriptionConstant'
import SubscriptionForm from '../../../subscription/components/subscriptionManagement/SubscriptionForm'
import {
  LABEL_LICENSES_PRODUCT,
  LABEL_LICENSES_SUBTENANT,
} from '../../constants/LabelConstants'
import { ROOT_NODE_ID, SUBSCRIPTION_ADD, TENANT_ADD, TENANT_EDIT } from '../../constants/TenancyConstants'
import TenancyService from '../../services/TenancyService'
import AddTenant from './AddTenant'
import './license.scss'
import LicenseEmptyScreen from './LicenseEmptyScreen'
import LicenseTable from './LicenseTable'
import ProductLicenseSubTenantTable from './ProductLicenseSubTenantTable'
import SummaryLicenseEmpty from './SummaryLicenseEmpty'
import AppContext from '../../../common/context/AppContext'
import Header from '../../../common/components/page/header/Header'
import { useMessage } from '../../../common/providers/MessageProvider'
import { setIsTenantMGMLoading } from '../../../common/reducers/actions/ViewReducer.actions'
import RenameInstanceModal from './Modal/RenameInstanceModal'
import { store, history, getTsgAccess } from '@sparky/framework'
import OneAppLicensePage from '../../../activation/components/OneAppLicensePage'
import { useIpRestrictionContext } from '../../../common/context/IpRestrictContext'
import IPRestrictionWrapper from '../../../common/components/noAccess/IPRestrictionWrapper'
import AcquisitionRequestBanner from '../acquireTenant/AcquisitionRequestBanner'
import GlobalContext from '../../../common/context/GlobalContext'
import SubtenantAcquisitionModal from '../acquireTenant/SubtenantAcquisitionModal'
import { FF_TENANT_ACQUISITIONS } from '../../../common/utils/FeatureFlags'
import useReadOnly from '../../../common/hooks/useReadOnly'
import { TSG_ACQUISITION_PERMISSION_SET } from '../../../iam/constants/IAMConstants'


const tsgMoveEnabled = getVarValue(FF_TENANT_ACQUISITIONS, false)

const Page = ({ pageHeaderProps, children, className = '' }) => {
  const { title, actions, ...restHeaderProps } = pageHeaderProps
  return (
    <div className={className}>
      <Header title={title} actions={actions} {...restHeaderProps} />
      {children}
    </div>
  )
}

/**
 *
 * @param breadcrumbs Breadcrumb obj
 * @param selectedObj Selected Tree Node
 * @param onBreadCrumbClick callback when uer click on the breadcrumb button
 * @returns {JSX.Element}
 * @constructor
 */
const Licenses = ({ breadcrumbs,
  selectedTreeNode,
  actionType,
  onBreadCrumbClick,
  actions,
  onCancel,
  isDeleteModalOpen,
  setDeleteModalOpen,
  setActions,
  dispatch,
  globalTsgId,
  sgKeyBy,
  supportedRegionsByKey,
  globalDispatch,
  popoverTenant,
  claimInfo,
  isTenantMgmLoading,
  isClaimLoading,
  ...rest
}) => {

  const { treeReducer = {} } = useContext(GlobalContext)
  const { acquisition_requests = [] } = treeReducer

  const { apps, auth } = rest
  const [isFormLoading, setFormLoading] = useState(false)
  const [error, setError] = useState('')
  const [licenseAction, setLicenseAction] = useState()
  const [licenseInfo, setLicenseInfo] = useState({})
  const [childLicensesLoading, setChildLicensesLoading] = useState(false)
  const [isInstanceLoading, setIsInstanceLoading] = useState(false)
  const renameTenantId = store.stateRef.current.commonModule?.path?.match(/\/rename\/(\w+)/)?.[1]
  const { openModal: openRenameModal } = useModal(RenameInstanceModal)
  const acquisition_id = new URLSearchParams(window.location.search).get('acquisition_id')
  const acqReadOnly = useReadOnly(selectedTreeNode?.id, TSG_ACQUISITION_PERMISSION_SET)

  const { dontRenderApp } = useContext(AppContext)
  const { createBanner } = useMessage()
  const { user } = auth
  const tsgRef = useRef()
  const enableProfileActivation = isVMFlexActivationEnabled(auth?.user?.supportAccountIds) //getVarValue(FF_ENABLE_PROFILE_ACTIVATION, false)
  const { setRestricted, checkIpRestricted } = useIpRestrictionContext()
  const subtenantAcquisitionModal = useModal(SubtenantAcquisitionModal)
  /**
   * Life cycles
   */
  useEffect(() => {
    dispatch(setNewTsgLoading({ newTsg: false, dayZero: false }))
  }, [dispatch])

  useEffect(() => {
    if (acquisition_id && !acqReadOnly) {
      tsgMoveEnabled && TenancyService.acquisitionRequests.get(selectedTreeNode?.id, acquisition_id)
        .then(({ data: acquisition }) => {
          subtenantAcquisitionModal.openModal({ acquisition })
          const url = new URL(window.location.href)
          url.searchParams.delete('acquisition_id')
          history.replace(url.pathname, history.state)
        })
        .catch(() => null) // fail silently
    }
  }, [selectedTreeNode?.id, acqReadOnly, acquisition_id, subtenantAcquisitionModal])

  useEffect(() => {
    const modalMask = document.getElementsByClassName('tw-bg-gray-900 tw-fixed tw-inset-0 tw-z-10 tw-transition-opacity tw-duration-200 tw-opacity-75')
    if (modalMask && modalMask.length) {
      modalMask[0].style.zIndex = 1051
    }
  }, [isDeleteModalOpen])

  const fetchInstances = useCallback(async (selectedTreeNode, dispatch) => {
    setRestricted(false)
    const childKeys = [...selectedTreeNode?.children?.map(each => each.id) || []]
    const allInstances = []
    const promises = []
    setIsInstanceLoading(true)
    promises.push(loadTsgInstances(selectedTreeNode?.id)
      .then(instances => {
        const isCurrentTsg = selectedTreeNode?.id === tsgRef.current?.id
        if (isCurrentTsg) {
          if (instances.error) {
            checkIpRestricted(instances.error)
          }
          else {
            allInstances.push(...instances)
            dispatch(loadInstanceData(allInstances))
          }
        }
      })
      .catch(error => {
        if (!checkIpRestricted(error)) {
          createBanner({ message: error?.message || error })
        }
      })
      .finally(() => {
        dispatch(setIsTenantMGMLoading(false))
        setIsInstanceLoading(false)
      })
    )
    if (childKeys && childKeys.length > 0) {
      setChildLicensesLoading(true)
      promises.push(LoadAllInstanceData(childKeys, selectedTreeNode?.id, dispatch, globalDispatch).then(childInstances => {
        const isCurrentTsg = selectedTreeNode?.id === tsgRef.current?.id
        if (isCurrentTsg) {
          if (childInstances.error) {
            checkIpRestricted(childInstances.error)
          }
          else {
            allInstances.push(...childInstances)
            dispatch(loadInstanceData(allInstances))
          }
        }
      })
        .catch(error => {
          if (!checkIpRestricted(error)) {
            createBanner({ message: error?.message || error })
          }
        })
        .finally(() => {
          setChildLicensesLoading(false)
        })
      )
    }
    await Promise.allSettled(promises)
  }, [setRestricted, createBanner, checkIpRestricted, globalDispatch])

  const fetchAcquisitions = useCallback(async (tsg_id) => {
    getTsgAccess(tsg_id).then(tsgAccess => {
      const access = tsgAccess?.checkPermissionSet(TSG_ACQUISITION_PERMISSION_SET)
      if (access === SPARKY_ACCESS_WRITE) {
        return Promise.all([
          LoadAcquisitions(tsg_id, dispatch),
          LoadAcqRequests(tsg_id, dispatch)
        ]).catch(error => {
          createBanner({ message: error.message })
        })
      }
    })
  }, [createBanner, dispatch])

  useEffect(() => {
    if ((selectedTreeNode?.id && tsgRef.current?.id !== selectedTreeNode?.id)) {
      tsgRef.current = selectedTreeNode
      dispatch({
        type: 'IS_TENANT_MGM_LOADING',
        isTenantMgmLoading: true
      })
      fetchInstances(selectedTreeNode, dispatch)
      tsgMoveEnabled && fetchAcquisitions(selectedTreeNode?.id)
    }
  }, [fetchInstances, selectedTreeNode, dispatch, fetchAcquisitions, acqReadOnly])

  const onRenameInstance = useCallback((isSuccess, instance, errorMessage = '') => {
    if (isSuccess) {
      createBanner({ message: `Successfully renamed ${instance.app.display_name}`, bannerProps: { appearance: 'success', type: 'page' } })
      fetchInstances(selectedTreeNode, dispatch)
    }
    else {
      createBanner({ message: errorMessage || 'An error occurred while renaming the instance. Please try again later' })
    }
  }, [createBanner, fetchInstances, dispatch, selectedTreeNode])

  useEffect(() => {
    const instance = (rest.instances.find(i => i.tenant_id === renameTenantId))
    const app = apps[instance?.app_id]
    if (instance && app) {
      openRenameModal({ instance, app, onRenameInstance })
      setCurrentViewing(VIEW_MANAGE_TENANT)
    }
  }, [rest.instances, renameTenantId, apps, onRenameInstance, openRenameModal])

  /**
   * On Submit save the form and go to default page
   * TODO: Add Service POST call
   * TODO: Add Loading Screen
   * TODO: Set state for default page
   * @type {(function(): void)|*}
   */
  const onSubmit = useCallback(async (formValue) => {
    try {
      setFormLoading(true)
      setError('')
      if (actionType === TENANT_ADD) {
        const parentTenant = popoverTenant || selectedTreeNode
        formValue = parentTenant?.id !== ROOT_NODE_ID ? { ...formValue, parent_id: parentTenant?.id, vertical: formValue.vertical } : { ...formValue }
        const newTsgDetails = await TenancyService.listTenant.post(formValue, parentTenant?.id !== ROOT_NODE_ID ? selectedTreeNode?.id : TSG_NONE.id)
        if (globalTsgId === TSG_NONE.id && newTsgDetails?.data) {
          await LoadTreeDataAsync(dispatch)
          await globalDispatch.switchTsg(newTsgDetails.data?.id, { forceReload: true })
          setCurrentViewing(VIEW_MANAGE_SUBSCRIPTION)
        }
        else {
          await LoadTreeDataAsync(dispatch)
            .then(() => dispatch(selectTreeNode({ parent_id: parentTenant?.id, ...newTsgDetails.data })))
        }
        setActions(undefined)
      }
      else if (actionType === TENANT_EDIT) {
        formValue = selectedTreeNode?.id !== ROOT_NODE_ID && selectedTreeNode.parent_id !== ROOT_NODE_ID ? { ...formValue, parent_id: selectedTreeNode.parent_id } : { ...formValue }
        await TenancyService.listTenant.put(selectedTreeNode?.id, formValue)
        LoadTreeData(dispatch)
        setActions(undefined)
      }
    }
    catch (e) {
      setError(e.message)
    }
    setFormLoading(false)
  }, [actionType, popoverTenant, selectedTreeNode, globalTsgId, dispatch, setActions, globalDispatch])


  useEffect(() => {
    setError('')
  }, [actionType])

  /**
   * Add Tenant
   * @type {(function(): void)|*}
   */
  const onAddLicenseProduct = useCallback(() => {
    setActions(TENANT_ADD)
  }, [setActions])

  const onAddSubTenant = useCallback(() => {
    setActions(TENANT_ADD)
  }, [setActions])

  const onAddLicenses = useCallback(() => {
    setLicenseAction('ADD')
    setActions(SUBSCRIPTION_ADD)
  }, [setActions])

  const onEditLicenses = useCallback(() => {
    setLicenseAction(SUBSCRIPTION_ACTION_EDIT)
    setActions(SUBSCRIPTION_ADD)
  }, [setActions])

  /**
   * Get Form values for edit prefilled
   * @type {{vertical: *, display_name: *, support_contact: *}}
   */
  const editFormValues = useMemo(() => {
    const parent_contact = sgKeyBy[selectedTreeNode?.parent_id]?.support_contact || ''
    const custom_contact = selectedTreeNode?.support_contact === parent_contact ? '' : selectedTreeNode?.support_contact
    const initial = selectedTreeNode?.isRoot || (Boolean(custom_contact) && custom_contact !== parent_contact) ? 'custom' : 'parent'
    const support_contact = {
      parent: parent_contact,
      custom: custom_contact,
      initial
    }
    return {
      display_name: selectedTreeNode?.display_name,
      vertical: { value: selectedTreeNode?.vertical },
      support_contact
    }
  }, [selectedTreeNode, sgKeyBy])

  /**
   * Get Form values for add prefilled
   * @type {{support_contact: *}}
   */
  const addFormValues = useMemo(() => {
    const parent_contact = selectedTreeNode?.support_contact || ''
    const support_contact = {
      parent: parent_contact,
      custom: '',
      initial: 'parent'
    }
    return { support_contact }
  }, [selectedTreeNode?.support_contact])

  const formValues = useMemo(() => {
    if (actionType === TENANT_EDIT) {
      return editFormValues
    }
    switch (actionType) {
      case TENANT_EDIT:
        return editFormValues
      case TENANT_ADD:
        return addFormValues
      default:
        return {}
    }
  }, [actionType, addFormValues, editFormValues])

  /**
   * Get parent and child licenses
   */
  const [parentLicense, childLicense] = useMemo(() => {
    const _childLicense = []
    const _parentLicense = (rest?.instancesKeyBy[selectedTreeNode?.id] || []).filter(({ app_id }) => {
      return app_id === APP_ID_PRISMA_ACCESS_EDITION || !dontRenderApp(app_id)
    })
    const licenseByKey = {}
    _parentLicense.forEach((each) => {
      licenseByKey[each.app_id] = each
      const supportAccountId = +each.support_account_id
      const signature = getSignature(supportAccountId, user?.supportAccountIds)
      const notonorafter = user?.notonorafter
      const xsig = signature ? `${supportAccountId} ${notonorafter} ${signature}` : null
      each.app = apps[each.app_id]
      each.xsig = xsig
      each.cobUsage = getCOBUsage(each.tenant_id, _parentLicense)
    })
    setLicenseInfo(licenseByKey)
    selectedTreeNode?.children?.forEach((each) => {
      const tsg_id = each.id
      if (dontRenderApp(each.app_id)) {
        return
      }
      if (rest?.instancesKeyBy[tsg_id]) {
        each.instances = rest.instancesKeyBy[tsg_id]
        each.instances.forEach((instance) => {
          const supportAccountId = +instance.support_account_id
          const signature = getSignature(supportAccountId, user?.supportAccountIds)
          const notonorafter = user?.notonorafter
          const xsig = signature ? `${supportAccountId}%20${notonorafter}%20${signature}` : null
          instance.app = apps[instance.app_id]
          instance.xsig = xsig
          instance.cobUsage = getCOBUsage(instance.tenant_id, each.instances)
        })
      }
      _childLicense.push({ ...each, tsg_id })
    })
    const filterHidden = instance => instance && (instance.extra?.showInTenantManagement || !(instance.extra?.hidden || instance.runtime_attributes?.hidden))

    return [transferLicenseToPA(_parentLicense).filter(filterHidden), transferChildLicenseToPA(_childLicense).filter(filterHidden)]
  }, [rest.instancesKeyBy, selectedTreeNode?.id, selectedTreeNode?.children, dontRenderApp, apps, user?.notonorafter, user?.supportAccountIds])

  const onDeleteInstance = useCallback((isSuccess, instance, errorMessage = '') => {
    if (isSuccess) {
      createBanner({ message: `Successfully deleted ${instance.app.display_name}`, bannerProps: { appearance: 'success', type: 'page' } })
      fetchInstances(selectedTreeNode, dispatch)
    }
    else {
      createBanner({ message: errorMessage || 'An error occurred while deleting the instance. Please try again later' })
    }
  }, [createBanner, fetchInstances, dispatch, selectedTreeNode])

  const getLicensePage = useMemo(() => {
    if (parentLicense && !parentLicense.length && childLicense && !childLicense.length) {
      return <LicenseEmptyScreen
        selectedTreeNode={selectedTreeNode}
        onAddLicenseProduct={onAddLicenseProduct}
        onAddSubTenant={onAddSubTenant}
        onAddLicenses={onAddLicenses}
        {...{ ...rest, isCgxPresent: claimInfo.isSDWANPresent, globalTsgId }} />
    }
    else if (parentLicense && !parentLicense.length && childLicense && Boolean(childLicense.length)) {
      return (
        <div className='tw-flex tw-flex-col tw-gap-2 tw-w-full tw-h-full tw-overflow-hidden'>
          <SummaryLicenseEmpty selectedTreeNode={selectedTreeNode} onAddSubTenant={onAddSubTenant}
            onAddLicenses={onAddLicenses} {...{ ...rest, isCgxPresent: claimInfo.isSDWANPresent }} />
          <ProductLicenseSubTenantTable
            isTableLoading={childLicensesLoading}
            selectedTreeNode={selectedTreeNode}
            globalDispatch={globalDispatch}
            dispatch={dispatch}
            title={LABEL_LICENSES_SUBTENANT}
            data={childLicense}
            supportedRegionsByKey={supportedRegionsByKey}
            onDeleteInstance={onDeleteInstance}
            sgKeyBy={sgKeyBy}
            {...{ ...rest }}
          />
        </div>
      )
    }
    else if (Boolean(parentLicense.length) && Boolean(childLicense.length)) {
      if (enableProfileActivation) {
        return (
          <div className='tw-flex tw-flex-col tw-gap-2 tw-w-full tw-h-full tw-overflow-auto'>
            <div>
              <OneAppLicensePage title={LABEL_LICENSES_PRODUCT}
                selectedTreeNode={selectedTreeNode}
                data={parentLicense}
                onEditLicenses={onEditLicenses}
                dispatch={dispatch}
                claimInfo={claimInfo}
                globalTsgId={globalTsgId}
                globalDispatch={globalDispatch}
                supportedRegionsByKey={supportedRegionsByKey}
                onDeleteInstance={onDeleteInstance}
                onRenameInstance={onRenameInstance}
                {...{ ...rest }}></OneAppLicensePage>
            </div>
            <ProductLicenseSubTenantTable
              isTableLoading={childLicensesLoading}
              selectedTreeNode={selectedTreeNode}
              globalDispatch={globalDispatch}
              dispatch={dispatch}
              title={LABEL_LICENSES_SUBTENANT}
              data={childLicense}
              supportedRegionsByKey={supportedRegionsByKey}
              onDeleteInstance={onDeleteInstance}
              onRenameInstance={onRenameInstance}
              sgKeyBy={sgKeyBy}
              {...{ ...rest }} />
          </div>
        )
      }
      else {
        return (
          <div className='tw-flex tw-flex-col tw-gap-2 tw-w-full tw-h-full tw-overflow-hidden'>
            <div className='tw-relative' style={{ flex: '0.5 0.5' }}>
              <LicenseTable
                title={LABEL_LICENSES_PRODUCT}
                selectedTreeNode={selectedTreeNode}
                data={parentLicense}
                onEditLicenses={onEditLicenses}
                dispatch={dispatch}
                claimInfo={claimInfo}
                globalTsgId={globalTsgId}
                globalDispatch={globalDispatch}
                supportedRegionsByKey={supportedRegionsByKey}
                onDeleteInstance={onDeleteInstance}
                onRenameInstance={onRenameInstance}
                {...{ ...rest }}
              />
            </div>
            <ProductLicenseSubTenantTable
              isTableLoading={childLicensesLoading}
              selectedTreeNode={selectedTreeNode}
              globalDispatch={globalDispatch}
              dispatch={dispatch}
              title={LABEL_LICENSES_SUBTENANT}
              data={childLicense}
              supportedRegionsByKey={supportedRegionsByKey}
              onDeleteInstance={onDeleteInstance}
              onRenameInstance={onRenameInstance}
              sgKeyBy={sgKeyBy}
              {...{ ...rest }} />
          </div>
        )
      }

    }
    else if (parentLicense && Boolean(parentLicense.length) && childLicense && !childLicense.length) {
      if (enableProfileActivation) {
        return <OneAppLicensePage
          title={LABEL_LICENSES_PRODUCT}
          dispatch={dispatch}
          selectedTreeNode={selectedTreeNode}
          data={parentLicense}
          claimInfo={claimInfo}
          globalTsgId={globalTsgId}
          onEditLicenses={onEditLicenses}
          globalDispatch={globalDispatch}
          supportedRegionsByKey={supportedRegionsByKey}
          onDeleteInstance={onDeleteInstance}
          onRenameInstance={onRenameInstance}
          {...{ ...rest }}
        />
      }
      else {
        return <LicenseTable
          title={LABEL_LICENSES_PRODUCT}
          dispatch={dispatch}
          selectedTreeNode={selectedTreeNode}
          data={parentLicense}
          claimInfo={claimInfo}
          globalTsgId={globalTsgId}
          onEditLicenses={onEditLicenses}
          globalDispatch={globalDispatch}
          supportedRegionsByKey={supportedRegionsByKey}
          onDeleteInstance={onDeleteInstance}
          onRenameInstance={onRenameInstance}
          {...{ ...rest }}
        />
      }

    }
    else {
      return <LicenseEmptyScreen
        selectedTreeNode={selectedTreeNode}
        onAddLicenseProduct={onAddLicenseProduct}
        onAddSubTenant={onAddSubTenant}
        onAddLicenses={onAddLicenses}
        {...{ ...rest, isCgxPresent: claimInfo.isSDWANPresent, globalTsgId }} />
    }
  }, [parentLicense, childLicense, selectedTreeNode, onAddLicenseProduct, onAddSubTenant, onAddLicenses, rest, claimInfo, globalTsgId, childLicensesLoading, globalDispatch, dispatch, supportedRegionsByKey, onDeleteInstance, sgKeyBy, enableProfileActivation, onEditLicenses, onRenameInstance])

  const acquisitionBanner = useMemo(() => {
    if (acqReadOnly) {
      return null
    }
    const request = acquisition_requests.find(acqRequest => acqRequest.status === 'pending' && acqRequest.approvers.some(approver => caseInsensitiveIsEqual(approver.email, user?.email)))
    if (request) {
      return tsgMoveEnabled ? (
        <div className='cm-p-1'>
          <AcquisitionRequestBanner tsg_id={selectedTreeNode?.id} acquisition={request}/>
        </div>
      ) : null
    }
  }, [acquisition_requests, acqReadOnly, selectedTreeNode?.id, user?.email])

  /**
   * Render page based on the different value
   * @type {unknown}
   */
  const getChildPage = useMemo(() => {
    switch (actionType) {
      case TENANT_EDIT:
        return <AddTenant
          setError={setError}
          error={error}
          isLoading={isFormLoading}
          onSubmit={onSubmit}
          onCancel={onCancel}
          actionType={actionType}
          selectedTreeNode={popoverTenant}
          formValues={formValues}
        />
      case TENANT_ADD:
        return <AddTenant
          setError={setError}
          error={error}
          isLoading={isFormLoading}
          onSubmit={onSubmit}
          onCancel={onCancel}
          selectedTreeNode={popoverTenant}
          actionType={actionType}
          formValues={formValues}
        />
      case SUBSCRIPTION_ADD:
        return <SubscriptionForm isLoading={isFormLoading}
          onSubmit={onSubmit}
          breadcrumbs={breadcrumbs}
          onClick={onBreadCrumbClick}
          onCancel={onCancel}
          supportedRegionsByKey={supportedRegionsByKey}
          selectedTreeNode={selectedTreeNode}
          globalTsgId={globalTsgId}
          {...{ ...rest, sgKeyBy, dispatch, setActions, globalDispatch, licenseAction, licenseInfo }}
          actionType={actionType} />
      case 'LICENSE_EMPTY':
        return <LicenseEmptyScreen
          selectedTreeNode={selectedTreeNode}
          onAddLicenseProduct={onAddLicenseProduct}
          onAddSubTenant={onAddSubTenant}
          onAddLicenses={onAddLicenses}
          {...rest} />
      default:
        return (
          <IPRestrictionWrapper>
            <Page
              className='cm-flex cm-flex-col cm-absolute cm-inset-0 cm-p-3'
              pageHeaderProps={{
                size: 'xs',
                title: <Bold>{selectedTreeNode?.display_name}</Bold>,
                level: 2,
                subTitle: selectedTreeNode?.vertical || '',
                actions: actions,
                selectedTreeNode
              }}>
              {!!acquisitionBanner && acquisitionBanner}
              {getLicensePage}
            </Page>
          </IPRestrictionWrapper>
        )
    }
  }, [actionType, error, isFormLoading, onSubmit, onCancel, popoverTenant, formValues, breadcrumbs,
    onBreadCrumbClick, supportedRegionsByKey, selectedTreeNode, globalTsgId, rest, sgKeyBy, dispatch, setActions,
    globalDispatch, licenseAction, licenseInfo, onAddLicenseProduct, onAddSubTenant,
    onAddLicenses, actions, getLicensePage, acquisitionBanner])

  return (
    <>
      {(isTenantMgmLoading || isInstanceLoading || isClaimLoading) && <div className='tw-w-full'><LoadingPage /></div>}
      {(!isTenantMgmLoading && !isInstanceLoading && !isClaimLoading) && (
        <div className={`${(actionType === SUBSCRIPTION_ADD) ? 'main-content tw-w-full' : 'main-content tw-w-full'}`}>
          <div className={`${(actionType === SUBSCRIPTION_ADD) ? 'tw-relative tw-h-full tw-overflow-auto' : 'tw-relative tw-flex tw-flex-col tw-flex-1 tw-h-full tw-overflow-auto'}`}>
            {getChildPage}
          </div>
        </div>
      )}
    </>
  )
}
export default Licenses
